import { useEffect, useState } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { ScrollText, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const SEV_COLOR = { low: "bg-slate-100 text-slate-700", medium: "bg-amber-50 text-amber-800", high: "bg-red-50 text-red-700" };
const STATUS_COLOR = { open: "bg-red-50 text-red-700", responded: "bg-amber-50 text-amber-800", closed: "bg-slate-100 text-slate-600", recovered: "bg-emerald-50 text-emerald-700" };

export default function Audit() {
  const { lang } = useLang();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const load = () => {
    const params = user?.panchayat_id ? { panchayat_id: user.panchayat_id } : {};
    api.get("/audit/observations", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [user]);

  const totalDisputed = rows.reduce((s, r) => s + r.amount_disputed, 0);
  const totalRecovered = rows.reduce((s, r) => s + r.recovered_amount, 0);

  return (
    <OfficerLayout>
      <div>
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">AuditOnline</div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-7 h-7 text-[#0A3161]" />
            {lang === "hi" ? "लेखा परीक्षा" : "Audit & Compliance"}
          </h1>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <Tile icon={AlertTriangle} label="Observations" value={rows.length} color="#E87722" />
          <Tile icon={AlertTriangle} label="Disputed Amount" value={formatINR(totalDisputed)} color="#0A3161" />
          <Tile icon={CheckCircle2} label="Recovered" value={formatINR(totalRecovered)} color="#107C10" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-3 rounded-md border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr><th className="text-left px-4 py-3">Obs. No.</th><th className="text-left px-4 py-3">FY</th>
                    <th className="text-left px-4 py-3">Type</th><th className="text-right px-4 py-3">Disputed</th>
                    <th className="text-left px-4 py-3">Severity</th><th className="text-left px-4 py-3">Status</th></tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <tr key={o.id} onClick={() => setPicked(o)} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${picked?.id === o.id ? "bg-[#0A3161]/5" : ""}`}>
                    <td className="px-4 py-3 font-mono text-xs">{o.obs_no}</td>
                    <td className="px-4 py-3 text-xs">{o.financial_year}</td>
                    <td className="px-4 py-3 capitalize text-xs">{o.audit_type}</td>
                    <td className="px-4 py-3 text-right font-mono">{formatINR(o.amount_disputed)}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded font-semibold ${SEV_COLOR[o.severity]}`}>{o.severity}</span></td>
                    <td className="px-4 py-3"><span className={`text-[10px] uppercase px-2 py-0.5 rounded font-semibold ${STATUS_COLOR[o.status]}`}>{o.status}</span></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-slate-400">No observations</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="lg:col-span-2">
            {picked ? <Detail obs={picked} onSaved={() => { load(); }} /> : (
              <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Select an observation</div>
            )}
          </div>
        </div>
      </div>
    </OfficerLayout>
  );
}

const Tile = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-md border border-slate-200 bg-white p-4 flex items-center gap-3">
    <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: color + "15", color }}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-0.5 font-heading text-xl font-bold text-slate-900">{value}</div>
    </div>
  </div>
);

function Detail({ obs, onSaved }) {
  const [status, setStatus] = useState(obs.status);
  const [response, setResponse] = useState(obs.response || "");
  const [recovered, setRecovered] = useState(obs.recovered_amount || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStatus(obs.status); setResponse(obs.response || ""); setRecovered(obs.recovered_amount || 0);
  }, [obs.id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/audit/observations/${obs.id}`, { status, response, recovered_amount: Number(recovered) });
      toast.success("Updated"); onSaved();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white p-5" data-testid="audit-detail">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#E87722] font-semibold">{obs.obs_no}</div>
      <div className="mt-2 text-sm font-semibold">{obs.observation}</div>
      <div className="mt-1 text-xs text-slate-500 capitalize">{obs.audit_type} · {obs.financial_year}</div>

      <div className="mt-4 space-y-3 text-sm">
        <Field label="Compliance Response">
          <textarea rows={3} value={response} onChange={(e) => setResponse(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="audit-response" />
        </Field>
        <Field label="Recovered Amount (₹)">
          <input type="number" value={recovered} onChange={(e) => setRecovered(e.target.value)}
            className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm font-mono" data-testid="audit-recovered" />
        </Field>
        <Field label="Status">
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm" data-testid="audit-status">
            {["open", "responded", "closed", "recovered"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <button onClick={save} disabled={busy} data-testid="audit-save"
          className="w-full px-3 py-2 text-xs font-semibold rounded-md text-white" style={{ background: "#0A3161" }}>
          {busy ? "…" : "Save"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">{label}</div>{children}</div>
);
